/**
 * Pano formu — toplayıcının okuyacağı yapılandırmayı üreten ekran.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * BU FORMDAKİ BİR HATA "VERİ GELMİYOR" DEMEKTİR.
 *
 * Eski sistemde bir panonun adresi `0.0.0.0` girilmişti; 7 gün boyunca 1.974
 * başarısız bağlantı denemesi üretti ve kimse fark etmedi. Paylaşılan şema
 * (`plcAdresi`) o değeri artık REDDEDİYOR — ama asıl kazanç, kullanıcının
 * bunu KAYDETMEDEN ÖNCE görmesi.
 *
 * İkinci tuzak: PLC'nin saha içi adresi (`plcHost`) erişim adresinden
 * AYRIDIR. Form onu sormasaydı kolon varsayılanı sessizce yazılır, pano
 * "yapılandırıldı" görünüp hiç okunamazdı (B adımının bulgusu).
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams, useSearch } from '@tanstack/react-router';
import { panelCreateSchema, type PanelCreateBody, type PanelCreateInput } from '@gasco/shared';

import { Field, Input, Select } from '../design/Field.tsx';
import {
  FormKabugu,
  IzinKapisi,
  KayitKapisi,
  KaydetmeHatasi,
  SecimYerTutucu,
  degisenAlanlar,
  sayiAlani,
  useKaydet,
} from './form-parts.tsx';
import { usePano, usePlcProfilleri, useSahalar, type PanoSatiri } from './api.ts';

export function PanoYeni(): React.JSX.Element {
  // Saha detayından gelindiyse saha ÖNCEDEN seçili gelir.
  const { siteId } = useSearch({ from: '/korumali/panolar/yeni' });
  return (
    <IzinKapisi gerekli="add_panel">
      <PanoFormu {...(siteId === undefined ? {} : { onSecilenSaha: siteId })} />
    </IzinKapisi>
  );
}

export function PanoDuzenle(): React.JSX.Element {
  const { panoId } = useParams({ from: '/korumali/panolar/$panoId/duzenle' });
  const sorgu = usePano(Number(panoId));

  return (
    <IzinKapisi gerekli="edit_panel">
      <KayitKapisi sorgu={sorgu} bulunamadiBaslik="Pano bulunamadı" donus="/panolar">
        {(pano) => <PanoFormu mevcut={pano} />}
      </KayitKapisi>
    </IzinKapisi>
  );
}

function baslangic(mevcut?: PanoSatiri, onSecilenSaha?: number): PanelCreateInput {
  return {
    siteId: mevcut?.site_id ?? onSecilenSaha ?? (undefined as unknown as number),
    serialNumber: mevcut?.serial_number ?? '',
    plcProfileId: mevcut?.plc_profile_id ?? (undefined as unknown as number),
    connectionMode: mevcut?.connection_mode ?? 'public',
    publicHost: mevcut?.public_host ?? '',
    tunnelIp: mevcut?.tunnel_ip ?? '',
    // Şemanın varsayılanı burada da GÖRÜNÜR — gizli varsayılan, yazılmamış
    // varsayılandan daha tehlikelidir.
    plcHost: mevcut?.plc_host ?? '192.168.1.10',
    plcPort: mevcut?.plc_port ?? 502,
    pollIntervalSeconds: mevcut?.poll_interval_seconds ?? 300,
    isPollingEnabled: mevcut?.is_polling_enabled ?? true,
  };
}

function PanoFormu({
  mevcut,
  onSecilenSaha,
}: {
  readonly mevcut?: PanoSatiri;
  readonly onSecilenSaha?: number;
}): React.JSX.Element {
  const duzenleme = mevcut !== undefined;
  const { kaydet, hata } = useKaydet('/panolar');
  const sahalar = useSahalar();
  const profiller = usePlcProfilleri();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<PanelCreateInput, unknown, PanelCreateBody>({
    resolver: zodResolver(panelCreateSchema),
    defaultValues: baslangic(mevcut, onSecilenSaha),
  });

  /*
   * Seçili mod hangi adresin ZORUNLU olduğunu belirler (`ck_panel_connection`
   * ve şemadaki `bağlantıDenetle` ile aynı kural). Kullanılmayan adres alanı
   * gizlenmiyor, yalnız zorunluluğu kalkıyor: geçiş sırasında iki adres de
   * dolu tutulabilmeli [K-005] — VPN'e geçen bir pano geri alınabilir olmalı.
   */
  const mod = watch('connectionMode');

  const gonder = handleSubmit(async (degerler) => {
    if (duzenleme) {
      /*
       * Mod ile adresi BİRLİKTE gönderiyoruz: sunucu yalnız `connectionMode`
       * gelirse yeni moda ait adresi göremez ve şema kontrolü haksız yere
       * reddeder (`panelUpdateSchema` yorumu).
       */
      const govde = degisenAlanlar(degerler, dirtyFields, [
        ['connectionMode', 'publicHost', 'tunnelIp'],
      ]);
      if (Object.keys(govde).length === 0) return;
      await kaydet(`/panels/${String(mevcut.id)}`, 'PATCH', govde);
      return;
    }
    await kaydet('/panels', 'POST', degerler);
  });

  return (
    <FormKabugu
      baslik={duzenleme ? 'Panoyu düzenle' : 'Yeni pano'}
      aciklama={duzenleme ? mevcut.serial_number : 'PLC bağlantı ayarları'}
      donus="/panolar"
      gonderiliyor={isSubmitting}
      onSubmit={(e) => void gonder(e)}
    >
      <Field label="Saha" required error={errors.siteId?.message}>
        {({ id, required, describedBy, invalid }) =>
          sahalar.data === undefined ? (
            <SecimYerTutucu id={id} describedBy={describedBy} />
          ) : (
            <Select
              id={id}
              required={required}
              aria-describedby={describedBy}
              invalid={invalid}
              {...register('siteId', sayiAlani)}
            >
              <option value="">Saha seçin</option>
              {sahalar.data.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.addr_code})
                </option>
              ))}
            </Select>
          )
        }
      </Field>

      <Field label="Seri numarası" required error={errors.serialNumber?.message}>
        {({ id, required, describedBy, invalid }) => (
          <Input
            id={id}
            required={required}
            aria-describedby={describedBy}
            invalid={invalid}
            {...register('serialNumber')}
          />
        )}
      </Field>

      <Field
        label="PLC profili"
        required
        hint="Register haritasını belirler [K-014]"
        error={errors.plcProfileId?.message}
      >
        {({ id, required, describedBy, invalid }) =>
          profiller.data === undefined ? (
            <SecimYerTutucu id={id} describedBy={describedBy} />
          ) : (
            <Select
              id={id}
              required={required}
              aria-describedby={describedBy}
              invalid={invalid}
              {...register('plcProfileId', sayiAlani)}
            >
              <option value="">Profil seçin</option>
              {profiller.data.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.brand_name}
                </option>
              ))}
            </Select>
          )
        }
      </Field>

      <Field
        label="Bağlantı türü"
        required
        hint="VPN geçişi pano pano yapılır ve geri alınabilir [K-005]"
        error={errors.connectionMode?.message}
      >
        {({ id, required, describedBy, invalid }) => (
          <Select
            id={id}
            required={required}
            aria-describedby={describedBy}
            invalid={invalid}
            {...register('connectionMode')}
          >
            <option value="public">Genel ağ</option>
            <option value="vpn">VPN</option>
          </Select>
        )}
      </Field>

      <Field
        label="Genel ağ adresi"
        required={mod === 'public'}
        hint={
          mod === 'public'
            ? 'Panoya internetten erişilen IP'
            : 'VPN modunda kullanılmıyor — geri dönüş için saklanabilir'
        }
        error={errors.publicHost?.message}
      >
        {({ id, required, describedBy, invalid }) => (
          <Input
            id={id}
            required={required}
            aria-describedby={describedBy}
            invalid={invalid}
            inputMode="decimal"
            placeholder="188.38.115.12"
            {...register('publicHost')}
          />
        )}
      </Field>

      <Field
        label="Tünel adresi"
        required={mod === 'vpn'}
        hint={mod === 'vpn' ? 'OpenVPN ağındaki adres' : 'VPN’e geçildiğinde gerekecek'}
        error={errors.tunnelIp?.message}
      >
        {({ id, required, describedBy, invalid }) => (
          <Input
            id={id}
            required={required}
            aria-describedby={describedBy}
            invalid={invalid}
            inputMode="decimal"
            placeholder="10.8.0.14"
            {...register('tunnelIp')}
          />
        )}
      </Field>

      <Field
        label="PLC adresi (saha içi)"
        required
        hint="Panonun kendi ağında PLC'nin adresi — erişim adresinden AYRIDIR"
        error={errors.plcHost?.message}
      >
        {({ id, required, describedBy, invalid }) => (
          <Input
            id={id}
            required={required}
            aria-describedby={describedBy}
            invalid={invalid}
            inputMode="decimal"
            {...register('plcHost')}
          />
        )}
      </Field>

      <Field label="PLC portu" required error={errors.plcPort?.message}>
        {({ id, required, describedBy, invalid }) => (
          <Input
            id={id}
            required={required}
            aria-describedby={describedBy}
            invalid={invalid}
            type="number"
            inputMode="numeric"
            {...register('plcPort', sayiAlani)}
          />
        )}
      </Field>

      <Field
        label="Yoklama aralığı (saniye)"
        required
        hint="En az 30 sn, en fazla 1 saat — 300 sn ≈ ayda 9 MB veri"
        error={errors.pollIntervalSeconds?.message}
      >
        {({ id, required, describedBy, invalid }) => (
          <Input
            id={id}
            required={required}
            aria-describedby={describedBy}
            invalid={invalid}
            type="number"
            inputMode="numeric"
            {...register('pollIntervalSeconds', sayiAlani)}
          />
        )}
      </Field>

      {/*
       * Yoklama kapalıysa bu panodan HİÇ veri gelmez. Onay kutusunun yardım
       * metni bunu açıkça söylüyor: liste ekranındaki "İzleme kapalı" uyarısı
       * ile aynı gerçeğin kaynağı burası.
       */}
      <div className="flex items-start gap-3">
        <input
          id="yoklama"
          type="checkbox"
          className="mt-1 size-5"
          {...register('isPollingEnabled')}
        />
        <label htmlFor="yoklama" className="text-sm">
          <span className="font-medium text-ink">Yoklama açık</span>
          <span className="block text-ink-muted">
            Kapalıyken bu panodan veri toplanmaz; sahada "İzleme kapalı" olarak görünür.
          </span>
        </label>
      </div>

      <KaydetmeHatasi hata={hata} />
    </FormKabugu>
  );
}
